import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Truck, Save, Loader as Loader2, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, RefreshCw, Settings, Key, Eye, EyeOff, Pencil } from 'lucide-react';

interface CircuitDriver {
  id: string;
  name: string;
  email: string;
  customName: string;
}

async function callCircuitProxy(action: string, circuitApiKey: string, body?: Record<string, unknown>) {
  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/circuit-proxy/${action}`;
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
  const payload = { ...body, circuitApiKey };
  const res = await fetch(apiUrl, { method: 'POST', headers, body: JSON.stringify(payload) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
  return data;
}

export default function CircuitSettings() {
  const { user } = useAuth();
  const [depotAddress, setDepotAddress] = useState('');
  const [circuitApiKey, setCircuitApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const [drivers, setDrivers] = useState<CircuitDriver[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driverError, setDriverError] = useState('');
  const [apiConnected, setApiConnected] = useState<boolean | null>(null);
  const [editingDriverId, setEditingDriverId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingDriverName, setSavingDriverName] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    if (!user?.unit_id) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('circuit_config')
        .select('depot_address, api_key')
        .eq('unit_id', user.unit_id)
        .maybeSingle();

      if (data) {
        setDepotAddress(data.depot_address || '');
        setCircuitApiKey(data.api_key || '');
        if (data.api_key) {
          testConnectionWithKey(data.api_key);
        }
      }
    } catch (err) {
      console.error('Error fetching circuit config:', err);
    } finally {
      setLoading(false);
    }
  };

  const testConnectionWithKey = async (key: string) => {
    if (!key) {
      setApiConnected(false);
      return;
    }
    setApiConnected(null);
    try {
      await callCircuitProxy('list-drivers', key);
      setApiConnected(true);
      fetchDriversWithKey(key);
    } catch {
      setApiConnected(false);
    }
  };

  const fetchDriversWithKey = async (key: string) => {
    setLoadingDrivers(true);
    setDriverError('');
    try {
      const [circuitData, customNamesResult] = await Promise.all([
        callCircuitProxy('list-drivers', key),
        user?.unit_id
          ? supabase
              .from('circuit_driver_names')
              .select('circuit_driver_id, display_name')
              .eq('unit_id', user.unit_id)
          : Promise.resolve({ data: [] }),
      ]);
      const namesMap = new Map(
        (customNamesResult.data || []).map((r: any) => [r.circuit_driver_id, r.display_name])
      );
      const driverList: CircuitDriver[] = (circuitData.drivers || []).map((d: any) => {
        const driverId = d.id || d.name;
        return {
          id: driverId,
          name: d.displayName || d.email || d.id,
          email: d.email || '',
          customName: namesMap.get(driverId) || '',
        };
      });
      setDrivers(driverList);
    } catch (err: any) {
      setDriverError(err.message || 'Erro ao carregar motoristas');
    } finally {
      setLoadingDrivers(false);
    }
  };

  const saveConfig = async () => {
    if (!user?.unit_id) return;
    setSaving(true);
    setSaved(false);
    try {
      const { data: existing } = await supabase
        .from('circuit_config')
        .select('id')
        .eq('unit_id', user.unit_id)
        .maybeSingle();

      const payload = {
        depot_address: depotAddress,
        api_key: circuitApiKey,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        await supabase
          .from('circuit_config')
          .update(payload)
          .eq('id', existing.id);
      } else {
        await supabase
          .from('circuit_config')
          .insert({ unit_id: user.unit_id, ...payload });
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);

      if (circuitApiKey) {
        testConnectionWithKey(circuitApiKey);
      }
    } catch (err) {
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  const saveDriverName = async (driverId: string) => {
    if (!user?.unit_id) return;
    setSavingDriverName(true);
    try {
      const trimmed = editingName.trim();
      const { data: existing } = await supabase
        .from('circuit_driver_names')
        .select('id')
        .eq('unit_id', user.unit_id)
        .eq('circuit_driver_id', driverId)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('circuit_driver_names')
          .update({ display_name: trimmed, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else if (trimmed) {
        await supabase
          .from('circuit_driver_names')
          .insert({ unit_id: user.unit_id, circuit_driver_id: driverId, display_name: trimmed });
      }

      setDrivers(prev => prev.map(d =>
        d.id === driverId ? { ...d, customName: trimmed } : d
      ));
      setEditingDriverId(null);
      setEditingName('');
    } catch (err) {
      console.error('Error saving driver name:', err);
    } finally {
      setSavingDriverName(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Settings className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configuracoes do Circuit</h1>
          <p className="text-sm text-gray-500">Gerencie a integracao com o Circuit para roteirizacao de motoboys</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-blue-600" />
            Chave de API do Circuit
          </h2>
          <p className="text-sm text-gray-500 mb-3">
            Obtenha sua chave em: getcircuit.com &gt; Settings &gt; Integrations &gt; API
          </p>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={circuitApiKey}
                onChange={e => setCircuitApiKey(e.target.value)}
                placeholder="Cole aqui sua API key do Circuit"
                className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
              <button
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                type="button"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Status da Conexao
          </h2>
          <div className="flex items-center gap-3">
            {!circuitApiKey ? (
              <>
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <span className="text-sm text-yellow-700">
                  Insira a API key acima e clique em Salvar para conectar.
                </span>
              </>
            ) : apiConnected === null ? (
              <>
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-sm text-gray-600">Verificando conexao com o Circuit...</span>
              </>
            ) : apiConnected ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">Conectado ao Circuit</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-600">
                  API key invalida ou erro de conexao. Verifique a chave e tente novamente.
                </span>
              </>
            )}
            {circuitApiKey && (
              <button
                onClick={() => testConnectionWithKey(circuitApiKey)}
                className="ml-auto p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Testar novamente"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <hr className="border-gray-200" />

        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Endereco Base (Deposito)
          </h2>
          <p className="text-sm text-gray-500 mb-3">
            Endereco de onde o motoboy sai para as entregas. Usado como ponto de partida da rota.
          </p>
          <input
            type="text"
            value={depotAddress}
            onChange={e => setDepotAddress(e.target.value)}
            placeholder="Ex: Rua das Flores, 123 - Centro - Sao Paulo/SP - 01000-000"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="pt-2">
          <button
            onClick={saveConfig}
            disabled={saving}
            className="w-full px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 font-medium"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : saved ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saved ? 'Configuracoes salvas!' : 'Salvar Configuracoes'}
          </button>
        </div>

        <hr className="border-gray-200" />

        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Motoristas Cadastrados
            </h2>
            <button
              onClick={() => fetchDriversWithKey(circuitApiKey)}
              disabled={loadingDrivers || !apiConnected}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingDrivers ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>

          {!apiConnected ? (
            <p className="text-sm text-gray-500">Configure a API key primeiro para ver os motoristas.</p>
          ) : loadingDrivers ? (
            <div className="flex items-center gap-2 py-4 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Carregando...</span>
            </div>
          ) : driverError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{driverError}</p>
            </div>
          ) : drivers.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <Truck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-600">
                Nenhum motorista cadastrado. Cadastre motoristas diretamente no painel do Circuit.
              </p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y">
              {drivers.map(d => (
                <div key={d.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Truck className="w-4 h-4 text-blue-600" />
                  </div>
                  {editingDriverId === d.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        placeholder="Nome do motorista"
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveDriverName(d.id);
                          if (e.key === 'Escape') { setEditingDriverId(null); setEditingName(''); }
                        }}
                      />
                      <button
                        onClick={() => saveDriverName(d.id)}
                        disabled={savingDriverName}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {savingDriverName ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Salvar
                      </button>
                      <button
                        onClick={() => { setEditingDriverId(null); setEditingName(''); }}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">
                          {d.customName || d.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {d.email}
                          {d.customName && d.name !== d.customName && (
                            <span className="ml-2 text-gray-400">({d.name})</span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => { setEditingDriverId(d.id); setEditingName(d.customName || ''); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Definir nome do motorista"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            Para adicionar/remover motoristas, acesse o painel do Circuit em getcircuit.com
          </p>
        </div>
      </div>
    </div>
  );
}
